import {
    Box,
    Button,
    Checkbox,
    Chip,
    FormControl,
    FormControlLabel,
    InputLabel,
    MenuItem,
    OutlinedInput,
    Select,
    Stack,
    Tooltip,
    Typography,
} from '@mui/material';
import {Container} from '@mui/system';
import React, {useContext, useEffect, useState} from 'react';
import {useNavigate, useParams} from 'react-router-dom';
import {createPost, deletePost, fetchOnePost} from '../http/postAPI';
import MySnackbar from '../components/MySnackbar';
import useSnackbar from '../hooks/useSnackbar';
import {getBranches} from '../http/clinicAPI';
import CancelIcon from '@mui/icons-material/Cancel';
import {DateTimePicker} from '@mui/x-date-pickers';
import {LocalizationProvider} from '@mui/x-date-pickers/LocalizationProvider';
import {AdapterDayjs} from '@mui/x-date-pickers/AdapterDayjs';
import dayjs from 'dayjs';
import Editor from '../components/Editor';
import {changeForDB, changeForEditor} from '../helpers/editorFunc';
import { validateImageFile, validateImageDimensions, ALLOWED_IMAGE_ACCEPT } from '../helpers/imageUpload';
import {Context} from '..';
import {LoadingButton} from "@mui/lab";
import {getImageUrl} from "../helpers/getImageUrl";
import InfoIcon from '@mui/icons-material/Info';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import useUnsavedChangesWarning from "../hooks/useUnsavedChangesWarning";

const API_URL = process.env.REACT_APP_API_URL || window.API_URL;

// Конфигурация меню для Select
const MenuProps = {
    PaperProps: {
        style: {
            maxHeight: 48 * 4.5 + 8,
            width: 250,
        },
    },
};

const Post = () => {
    // Состояние компонента
    const [post, setPost] = useState({}); // { id, content, type, publishDateTime, validUntil, notifyBeforeDays, branches, exampleService, image, status, authorId }
    const [branches, setBranches] = useState([]); // [{ id, name, isActive }]
    const [services, setServices] = useState([]); // [{ id, name }]
    const [selectedBranchesNames, setSelectedBranchesNames] = useState([]);
    const [selectedService, setSelectedService] = useState('');
    const [image, setImage] = useState(null);
    const [postImage, setPostImage] = useState(null);
    const [btnSaveDisabled, setBtnSaveDisabled] = useState(true);
    const [publishDateTime, setPublishDateTime] = useState(null);
    const [publishDeferred, setPublishDeferred] = useState(false);
    const [validUntil, setValidUntil] = useState(null);
    const [notifyOnExpiration, setNotifyOnExpiration] = useState(false);
    const [notifyBeforeDays, setNotifyBeforeDays] = useState('');
    const [hasUnsaved, setHasUnsaved] = useState(false);

    const [isPublishing, setPublishing] = useState(false);

    const params = useParams();
    const navigate = useNavigate();
    const snack = useSnackbar(false, 'Ошибка');
    const parentPage = document.location.pathname.split('/');
    const isNew = params.id === 'new';
    const pageType = parentPage[1];
    const isNewsOrPromotion = pageType === 'news' || pageType === 'promotions';
    const typeTitles = {
        news: 'новости',
        promotions: 'акции',
        examples: 'примера',
    };
    const pageTitle = `${isNew ? 'Создание' : 'Редактирование'} ${typeTitles[pageType] || 'поста'}`;
    const { confirmLeave } = useUnsavedChangesWarning(hasUnsaved);

    const {user} = useContext(Context);

    useEffect(() => {
        fetchBranches();

        if (!isNew) {
            fetchPostData(params.id);
        }
    }, []);

    useEffect(() => {
        // Сбрасываем поля, которые не должны использоваться для новостей
        if (pageType === 'news') {
            setValidUntil(null);
            setNotifyOnExpiration(false);
            setNotifyBeforeDays('');
        }
    }, [pageType]);

    const fetchBranches = async () => {
        const data = await getBranches();
        setBranches(data);
    };

    const fetchPostData = async (id) => {
        const data = await fetchOnePost(id);
        data.content = changeForEditor(data.content);
        setPost((prev) => ({...prev, ...data}));
        setPublishDateTime(data.scheduledAt ? dayjs(data.scheduledAt, 'YYYY-MM-DD HH:mm:ss') : null);
        setPublishDeferred(data.scheduledAt != null);
        setValidUntil(data.validUntil ? dayjs(data.validUntil, 'YYYY-MM-DD HH:mm:ss') : null);
        setNotifyOnExpiration(!!data.notifyBeforeDays);
        setNotifyBeforeDays(data.notifyBeforeDays || '');
        setSelectedBranchesNames(data.branches?.map((branch) => branch.name) || []);
        setSelectedService(data.exampleService?.name || '');
        setImage(data.image ? API_URL + '/static/' + data.image : null);
        setHasUnsaved(false);
    };

    const handleImageUpload = async (file) => {
        const result = validateImageFile(file);
        if (!result.valid) { snack.handleChange(true, result.error); return; }
        const dimResult = await validateImageDimensions(file, 2560);
        if (!dimResult.valid) { snack.handleChange(true, dimResult.error); return; }
        setImage(URL.createObjectURL(file));
        setPostImage(file);
        setBtnSaveDisabled(false);
        setHasUnsaved(true);
    };

    const removeImage = () => {
        setImage(null);
        setPostImage(null);
        setBtnSaveDisabled(true);
        setHasUnsaved(true);
    };

    const handleChange = (event) => {
        const files = Array.from(event.target.files || []);
        if (files[0]) {
            handleImageUpload(files[0]);
        }
    };

    const removePost = async () => {
        await deletePost({id: params.id});
        navigate('/' + pageType);
    };

    const save = async ({publishNow = false, toArchive = false, publishScheduled = false}) => {
        setPublishing(true);
        let content = changeForDB(post.content);

        if (!content || content.length === 0) {
            snack.handleChange(true, 'Введите текст поста');
            return;
        }

        if (selectedBranchesNames.length === 0) {
            snack.handleChange(true, 'Выберите хотя бы один филиал для публикации');
            return;
        }

        if (
            pageType === 'promotions' &&
            publishDeferred &&
            validUntil &&
            publishDateTime &&
            validUntil.isBefore(publishDateTime)
        ) {
            snack.handleChange(true, 'Дата действия акции не может быть раньше даты публикации');
            return;
        }

        if (pageType === 'promotions' && validUntil && validUntil.isBefore(dayjs())) {
            snack.handleChange(true, 'Дата действия акции не может быть в прошлом');
            return;
        }

        if (
            pageType === 'promotions' &&
            notifyOnExpiration &&
            (!notifyBeforeDays || Number(notifyBeforeDays) < 1)
        ) {
            snack.handleChange(true, 'Укажите количество дней для уведомления (не менее 1)');
            return;
        }

        if (
            pageType === 'promotions' &&
            notifyOnExpiration &&
            validUntil &&
            publishDateTime &&
            notifyBeforeDays &&
            Number(notifyBeforeDays) > validUntil.diff(publishDateTime, 'day')
        ) {
            snack.handleChange(true, 'Количество дней для уведомления превышает срок действия акции');
            return;
        }

        const formData = new FormData();
        if (!isNew) {
            formData.append('id', params.id);
        }
        formData.append('content', content);
        formData.append('type', pageType);

        formData.append(
            'publishDateTime',
            publishDeferred ? publishDateTime.format('YYYY-MM-DD HH:mm:ss') : null
        );

        if (pageType === 'promotions' && validUntil) {
            formData.append('validUntil', validUntil.format('YYYY-MM-DD HH:mm:ss'));
        }

        if (pageType === 'promotions' && notifyOnExpiration && notifyBeforeDays) {
            formData.append('notifyBeforeDays', notifyBeforeDays);
        }

        formData.append('publishNow', publishNow.toString());
        formData.append('publishScheduled', publishScheduled.toString());
        formData.append('toArchive', toArchive.toString());

        const postBranches = selectedBranchesNames.map((selectedBranch) =>
            branches.find((branch) => branch.name === selectedBranch)
        );

        formData.append('branches', JSON.stringify(postBranches || []));

        if (pageType === 'examples') {
            formData.append('serviceId', services.find((s) => s.name === selectedService)?.id || '');
        }

        if (postImage) {
            formData.append('image', postImage);
        }

        formData.append('authorId', user.user.id);

        await createPost(formData);
        setPublishing(false);
        setHasUnsaved(false);
        navigate('/' + pageType);
    };

    const handleSelectBranch = (event) => {
        const {value} = event.target;
        setSelectedBranchesNames(typeof value === 'string' ? value.split(',') : value);
        setBtnSaveDisabled(false);
        setHasUnsaved(true);
    };

    const deleteSelectedBranch = (branchName) => {
        setSelectedBranchesNames(selectedBranchesNames.filter((branch) => branch !== branchName));
        setBtnSaveDisabled(false);
        setHasUnsaved(true);
    };

    const handleSelectService = (event) => {
        const {value} = event.target;
        setSelectedService(value);
        setBtnSaveDisabled(false);
        setHasUnsaved(true);
    };

    const handleBack = () => {
        if (!confirmLeave()) {
            return;
        }
        navigate(-1);
    };

    return (
        <Container
            sx={{
                display: 'flex',
                alignItems: 'flex-start',
                flexDirection: 'column',
                py: 3,
                maxWidth: 960,
            }}
        >
            <MySnackbar {...snack} />

            <Stack spacing={1} sx={{mb: 3, width: '100%'}}>
                <Button
                    variant='text'
                    size='small'
                    startIcon={<ArrowBackIcon />}
                    sx={{alignSelf: 'flex-start'}}
                    onClick={handleBack}
                >
                    Назад
                </Button>
                <Typography variant='h4' fontWeight={600}>
                    {pageTitle}
                </Typography>
                {!isNew && (
                    <Typography variant='body2' component={'span'} color='templateText.secondary'>
                        Статус:
                        <Chip
                            size='small'
                            color={
                                post.status == null || post.status === 0
                                    ? 'success'
                                    : post.status === 1
                                        ? 'primary'
                                        : post.status === 2
                                            ? 'error'
                                            : 'warning'
                            }
                            label={
                                post.status == null || post.status === 0
                                    ? 'Опубликовано'
                                    : post.status === 1
                                        ? 'Сохранено'
                                        : post.status === 2
                                            ? 'В архиве'
                                            : 'Отложено'
                            }
                            sx={{ml: 0.5}}
                        />
                    </Typography>
                )}
            </Stack>

            <Editor
                value={post.content}
                setValue={(value) => {
                    if (value.length > 1000) {
                        snack.handleChange(true, 'Количество символов до 1000');
                        return;
                    }
                    setPost((prev) => ({...prev, content: value}));
                    setBtnSaveDisabled(false);
                    setHasUnsaved(true);
                }}
                placeholder={pageType === 'news' ? 'Введите описание новости' : 'Введите описание акции'}
                sx={{mb: 2, width: '100%'}}
            />

            {pageType !== 'examples' && (
                <Stack direction='row' justifyContent={'flex-start'} gap={2} alignItems={'center'} mb={2} sx={{width: '100%'}}>
                    <FormControl sx={{width: '100%', maxWidth: 480}} error={selectedBranchesNames.length === 0}>
                        <InputLabel id='branchesLabel' error={selectedBranchesNames.length === 0}
                                    shrink>
                            {/*{pageType === 'news' ? 'Филиалы, в которых отображается данная новость' : 'Филиалы, в которых отображается данная акция'}*/}
                            Филиалы
                        </InputLabel>
                        <Select
                            labelId='branchesLabel'
                            multiple
                            fullWidth
                            value={selectedBranchesNames}
                            onChange={handleSelectBranch}
                            input={<OutlinedInput id='select-multiple-chip' label='Филиалы'/>}
                            renderValue={(selected) => (
                                <Box sx={{display: 'flex', flexWrap: 'wrap', gap: 0.5}}>
                                    {selected.map((branchName) => (
                                        <Chip
                                            key={branchName}
                                            deleteIcon={<CancelIcon onMouseDown={(event) => event.stopPropagation()}/>}
                                            label={branches?.find((branch) => branch.name === branchName)?.name}
                                            onDelete={() => deleteSelectedBranch(branchName)}
                                            clickable
                                        />
                                    ))}
                                </Box>
                            )}
                            MenuProps={MenuProps}
                            label="Филиалы"
                        >
                            {branches
                                .filter((branch) => branch.isActive !== false)
                                .map((branch) => (
                                    <MenuItem key={branch.id} value={branch.name}>
                                        {branch.name}
                                    </MenuItem>
                                ))}
                        </Select>


                    </FormControl>

                </Stack>

            )}

            {pageType === 'examples' && (
                <FormControl sx={{mb: 2, width: '100%', maxWidth: 480}}>
                    <InputLabel id='servicesLabel'>Услуга</InputLabel>
                    <Select
                        labelId='servicesLabel'
                        fullWidth
                        value={selectedService}
                        onChange={handleSelectService}
                        input={<OutlinedInput id='select-multiple-chip' label='Chip'/>}
                        MenuProps={MenuProps}
                    >
                        {services.map((service) => (
                            <MenuItem key={service.id} value={service.name}>
                                {service.name}
                            </MenuItem>
                        ))}
                    </Select>
                </FormControl>
            )}

            <Box sx={{mb: 2, width: '100%'}}>
                {
                    image ? (
                        <img width='120' src={image || ''} alt='Post'/>
                    ) : (
                        <Box sx={{
                            opacity: 0.5,
                            pointerEvents: 'none',
                            userSelect: 'none',
                            border: '1px dashed',
                            display: 'flex',
                            flexDirection: 'row',
                            alignItems: 'center',
                            justifyContent: 'center',
                        }}
                             padding={2} width='100%' maxWidth='520px'>
                            <img style={{marginRight: '12px'}} width='100' src={getImageUrl('undefined.jpg')}
                                 alt='Post'/>
                            <Stack>
                                <Typography variant='body1' fontWeight={'500'}>
                                    Нет изображения
                                </Typography>
                                <Typography variant='body2' sx={{opacity: 0.75}}>
                                    При просмотре в боте будет отображаться стандартное
                                    изображение
                                </Typography>
                            </Stack>
                        </Box>
                    )
                }

            </Box>
            {
                image ? (
                    <Button variant='outlined' color='error' sx={{mb: 2}} onClick={removeImage}>
                        Убрать картинку
                    </Button>
                ) : (
                    <Button variant='outlined' component='label' sx={{mb: 2}}>
                        Выбрать картинку
                        <input hidden accept={ALLOWED_IMAGE_ACCEPT} type="file" onChange={handleChange}/>
                    </Button>
                )
            }


            {['promotions', 'news'].includes(pageType) && (
                <Box sx={{display: 'flex', flexDirection: 'column', gap: 1, mb: 2}}>
                    <FormControlLabel
                        control={
                            <Checkbox
                                checked={publishDeferred}
                                onChange={(event) => {
                                    setPublishDeferred(event.target.checked);
                                    setHasUnsaved(true);
                                }}
                            />
                        }
                        label='Отложенная публикация'
                    />
                    <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale='ru'>
                        {publishDeferred && (
                            <Box sx={{display: 'flex', alignItems: 'center', gap: 2}}>
                                <DateTimePicker
                                    sx={{width: '100%', maxWidth: 480, mb: 1}}
                                    label='Отложенная публикация'
                                    value={publishDateTime}
                                    format='DD.MM.YYYY HH:mm'
                                    ampm={false}
                                    onChange={(newPublishDateTime) => {
                                        setBtnSaveDisabled(false);
                                        setHasUnsaved(true);
                                        const fixed = dayjs(newPublishDateTime.format('YYYY-MM-DD HH:mm:ss'), 'YYYY-MM-DD HH:mm:ss');
                                        setPublishDateTime(fixed);
                                    }}
                                    minDateTime={dayjs()}
                                />

                                <Tooltip
                                    title={'Публикация отправляется каждому пациенту в зависимости от времени его выбранного филиала'}
                                    arrow>
                                    <InfoIcon sx={{opacity: 0.75}} fontSize='small'/>
                                </Tooltip>
                            </Box>
                        )}
                        {pageType === 'promotions' && (
                            <Box sx={{display: 'flex', alignItems: 'center', gap: 2}}>

                                <DateTimePicker
                                    sx={{width: '100%', maxWidth: 480}}
                                    label='Действительна до'
                                    format='DD.MM.YYYY HH:mm'
                                    ampm={false}
                                    value={validUntil}
                                    onChange={(newValidUntil) => {
                                        setBtnSaveDisabled(false);
                                        setHasUnsaved(true);
                                        const fixed = dayjs(newValidUntil.format('YYYY-MM-DD HH:mm:ss'), 'YYYY-MM-DD HH:mm:ss');
                                        setValidUntil(fixed ? fixed : null);
                                        if (!newValidUntil) {
                                            setNotifyOnExpiration(false);
                                            setNotifyBeforeDays('');
                                        }
                                    }}
                                    minDateTime={publishDeferred ? publishDateTime || dayjs() : dayjs()}
                                />
                                <Tooltip
                                    title={'Публикация удаляется у каждого пациента в зависимости от времени его выбранного\n' +
                                        '                                    филиала.'}
                                    arrow>
                                    <InfoIcon sx={{opacity: 0.75}} fontSize='small'/>
                                </Tooltip>
                                {
                                    validUntil && (
                                        <Button onClick={() => setValidUntil(null)}>Очистить</Button>
                                    )
                                }
                            </Box>
                        )}
                    </LocalizationProvider>
                </Box>
            )}

            {!publishDeferred && (
                <Stack alignItems='center' direction='row' gap={1} mb={1}>
                    <LoadingButton
                        variant='contained'
                        loading={isPublishing}
                        disabled={!post.content || selectedBranchesNames.length === 0}
                        disableElevation={true}
                        onClick={() => save({publishNow: true})}
                    >
                        {post?.id ? 'Сохранить и опубликовать' : 'Опубликовать с уведомлением'}
                    </LoadingButton>
                    {isNewsOrPromotion && (
                        <Tooltip title={'Станет видимой в боте и придёт уведомление'} arrow>
                            <InfoIcon sx={{opacity: 0.75}} fontSize='small'/>
                        </Tooltip>
                    )}
                </Stack>
            )}
            {
                publishDeferred && (
                    <LoadingButton
                        variant='outlined'
                        loading={isPublishing}
                        disabled={!(dayjs(publishDateTime).isValid() && dayjs(publishDateTime).isAfter(dayjs())) || btnSaveDisabled || !post.content || selectedBranchesNames.length === 0}
                        sx={{mb: 1}}
                        disableElevation={true}
                        onClick={() => save({publishScheduled: true})}
                    >
                        Отложенная публикация
                    </LoadingButton>
                )
            }

            <Stack alignItems='center' direction='row' gap={1} mb={1}>
                <LoadingButton
                    variant='outlined'
                    loading={isPublishing}
                    disabled={btnSaveDisabled || !post.content || selectedBranchesNames.length === 0}
                    disableElevation={true}
                    onClick={() => save({})}
                >
                    {post?.id ? 'Сохранить изменения' : 'Опубликовать'}
                </LoadingButton>
                <Tooltip
                    title={'Станет видимым в боте, но без уведомления'}
                    arrow>
                    <InfoIcon sx={{opacity: 0.75}} fontSize='small'/>
                </Tooltip>
            </Stack>

            <Stack alignItems='center' direction='row' gap={1} mb={1}>
                <LoadingButton
                    variant='outlined'
                    color='error'
                    loading={isPublishing}
                    disabled={!post.content}
                    disableElevation={true}
                    onClick={() => save({toArchive: true})}
                >
                    Архивировать
                </LoadingButton>
                {isNewsOrPromotion && (
                    <Tooltip
                        title={
                            isNew
                                ? 'Сохранится в админ.панели, но в боте не будет отображаться'
                                : 'Удалится из бота, но останется в админ.панели'
                        }
                        arrow
                    >
                        <InfoIcon sx={{opacity: 0.75}} fontSize='small'/>
                    </Tooltip>
                )}
            </Stack>
            {!isNew && (
                <Button
                    variant='contained'
                    color='error'
                    sx={{mb: 1}}
                    disableElevation={true}
                    onClick={removePost}
                >
                    Удалить
                </Button>
            )}
        </Container>
    );
};

export default Post;
